/**
 * Cookie consent — single source of truth.
 *
 * Storage key:    ads_dashbound_cookie_consent
 * Storage shape:  { necessary, analytics, marketing, preferences, timestamp, version }
 *
 * Conventions:
 *   - `necessary` is always true and cannot be disabled.
 *   - When the stored `version` does not match `CONSENT_VERSION`, the user is
 *     treated as "undecided" (so we can re-prompt after policy changes).
 *   - Every state change dispatches a `window` event:
 *       'ads-dashbound:consent-changed'
 *     so script integrators (GA, Clarity, Meta Pixel, etc.) can react without
 *     coupling to React.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const CONSENT_KEY = 'ads_dashbound_cookie_consent';
export const CONSENT_VERSION = '1.0';
export const CONSENT_EVENT = 'ads-dashbound:consent-changed';

export const DEFAULT_CONSENT = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
});

export const CATEGORIES = [
  {
    key: 'necessary',
    title: 'Necessary Cookies',
    description:
      'Required for core site functionality, security, and form handling. These cannot be disabled.',
    locked: true,
  },
  {
    key: 'analytics',
    title: 'Analytics Cookies',
    description:
      'Help us understand page visits, traffic sources, and site performance.',
    locked: false,
  },
  {
    key: 'marketing',
    title: 'Marketing Cookies',
    description:
      'Help measure campaign activity and support advertising-related tracking.',
    locked: false,
  },
  {
    key: 'preferences',
    title: 'Preferences Cookies',
    description:
      'Remember choices such as display settings and cookie preferences.',
    locked: false,
  },
];

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/** Read & validate the stored consent object. */
export function readStoredConsent() {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      necessary: true, // always true
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      preferences: !!parsed.preferences,
      timestamp: parsed.timestamp || null,
      version: parsed.version,
    };
  } catch {
    return null;
  }
}

/** Persist consent + emit a global event for script integrators. */
export function writeStoredConsent(partial) {
  if (!isBrowser()) return null;
  const next = {
    necessary: true,
    analytics: !!partial.analytics,
    marketing: !!partial.marketing,
    preferences: !!partial.preferences,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
  } catch {
    /* quota / disabled storage — fail soft */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: next }),
    );
  } catch {
    /* CustomEvent may not exist in older browsers */
  }
  return next;
}

/** Public helper for non-React consumers (script bootstrap, etc.). */
export function getConsent() {
  return readStoredConsent() || { ...DEFAULT_CONSENT, version: CONSENT_VERSION };
}

/* ----------------------------------------------------------------
   React context
   ---------------------------------------------------------------- */

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readStoredConsent());
  const [managerOpen, setManagerOpen] = useState(false);
  // Keep a ref to broadcast stable callbacks even after async state updates.
  const consentRef = useRef(consent);
  consentRef.current = consent;

  // Listen to changes from other tabs.
  useEffect(() => {
    if (!isBrowser()) return undefined;
    function onStorage(e) {
      if (e.key !== CONSENT_KEY) return;
      setConsent(readStoredConsent());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const decided = !!consent;

  const save = useCallback((partial) => {
    const next = writeStoredConsent({
      analytics: partial?.analytics ?? false,
      marketing: partial?.marketing ?? false,
      preferences: partial?.preferences ?? false,
    });
    setConsent(next);
    return next;
  }, []);

  const acceptAll = useCallback(
    () => save({ analytics: true, marketing: true, preferences: true }),
    [save],
  );
  const rejectNonEssential = useCallback(
    () => save({ analytics: false, marketing: false, preferences: false }),
    [save],
  );

  const openManager = useCallback(() => setManagerOpen(true), []);
  const closeManager = useCallback(() => setManagerOpen(false), []);

  const value = useMemo(
    () => ({
      consent: consent || { ...DEFAULT_CONSENT, version: CONSENT_VERSION },
      decided,
      save,
      acceptAll,
      rejectNonEssential,
      managerOpen,
      openManager,
      closeManager,
    }),
    [consent, decided, save, acceptAll, rejectNonEssential, managerOpen, openManager, closeManager],
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used inside <ConsentProvider>');
  }
  return ctx;
}
